import z from 'zod';
import { Draft } from '../common';
import { getAddressSchema } from '../address';
import { IFieldErrors, UrlSchema, ValidationError } from '../validation';
import { IOrgModel } from './orgInterface';
import { EmailSchema } from '../validation';
import { phoneSchema } from './common/phoneSchema';

/**
 * All non-conditional fields
 */
const Level1Base = z.object({
  name: z.string().min(1), //institution name
  instLvlCd: z
    .literal(-3, {
      errorMap: () => ({ message: 'You must select an institution level' }),
    })
    .or(z.literal(1))
    .or(z.literal(2))
    .or(z.literal(3)),
  email: EmailSchema,
  phone: phoneSchema,
  fax: phoneSchema.optional(),
  website: UrlSchema,
});

/**
 * Validation Schema Generator
 *
 * This is a common piece of export from a standard schema file.  This function operates as
 * a protected method that should only be used to build parent or child schemas in combination
 * with other standard validation.  It should never be exposed to service logic.  The other
 * method `validate()` should be the expected interface when used in business logic.
 * This helps isolate the scope of impact of the underlying validation library.  None of our
 * service logic should change in the event we swap the validation library.
 */
export const getSchema = (update: Draft<IOrgModel>, type: string) => {
  const { visitingAddress } = update;
  const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);

  return Level1Base.extend({
    orgId: type === 'update' ? z.string().min(1) : z.undefined(),

    visitingAddress: VisitingAddressSchema,
  });
};

/**
 * Execution of Validation Rules
 *
 * The major expectation from the schema file.  This is the true public export
 * which will commonize the input and output into an application driven abstraction.
 * If this is the only function to reach the service layer, replacing the library will
 * be far more approachable as any logic leaning on this will not have proprietary output
 * in either return type or exception
 */
export const validate = (update: Draft<IOrgModel>, type: string) => {
  const schema = getSchema(update, type);
  try {
    // Ensure there are changes from the original record
    return schema.parse(update);
  } catch (error: any) {
    // console.log('ORG ZOD Error : ', error);
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();

      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};
